import React, { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import {
  DataGridPro,
  GridToolbar,
  GridCellEditStopReasons,
} from "@mui/x-data-grid-pro";
import colors from "styles/colors";

function isKeyboardEvent(event) {
  return !!event.key;
}

export const EditableDataGridPro = (props) => {
  const {
    apiRef,
    columns,
    rows,
    loading,
    keepFocusOnEnter,
    getRowId,
    save,
    canEdit = true,
    userEmailAddress,
    enableSaveAll = true,
    isEditClicked = false,
    saveRowChange,
    cancelRowChange,
    getRowClassName,
  } = props;
  const [isSaving, setIsSaving] = useState(false);

  const handleOnCellClick = (params) => {
    if (
      params.cellMode === "view" &&
      (params.field === "comment" || params.field === "taggedUsers") &&
      params.row.commentBy === userEmailAddress
    ) {
      apiRef.current.startCellEditMode({
        id: params.id,
        field: params.field,
      });
    }
  };

  const handleOnCellEditStop = (params, event) => {
    if (!keepFocusOnEnter) {
      return;
    }

    if (params.reason !== GridCellEditStopReasons.enterKeyDown) {
      return;
    }

    if (isKeyboardEvent(event) && !event.ctrlKey && !event.metaKey) {
      event.defaultMuiPrevented = true;
    }
  };

  const CustomToolbar = () => {
    const handleOnClick = () => {
      setIsSaving(true);
      save().finally(() => setIsSaving(false));
    };

    return (
      <Box display="flex" justifyContent={"end"}>
        <GridToolbar sx={{ flexGrow: 1 }} />
        {canEdit && !isEditClicked && enableSaveAll && (
          <Button
            onClick={handleOnClick}
            sx={{
              mt: 1,
              color: "white",
              backgroundColor: colors.coreColors.jllRed.core,
              mr: 1,
            }}
            variant="contained"
            size="small"
          >
            {isSaving ? <CircularProgress size={14} /> : "Save"}
          </Button>
        )}
        {isEditClicked && canEdit && (
          <Box sx={{ display: "flex", justifyContent: "end" }}>
            <Button
              onClick={cancelRowChange}
              sx={{
                mt: 1,
                color: "#00151D",
                backgroundColor: colors.coreColors.white,
                mr: 1,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.08)",
                },
              }}
              variant="text"
              size="small"
            >
              Cancel
            </Button>
            <Button
              onClick={saveRowChange}
              sx={{
                mt: 1,
                color: "#00151D",
                backgroundColor: "var(--secondary-main, #EDF3F4)",
                mr: 1,
                "&:hover": {
                  backgroundColor: "var(--secondary-dark, #D1D9DA)",
                },
              }}
              variant="text"
              size="small"
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <DataGridPro
      initialState={{
        pinnedColumns: {
          right: ["actions"],
        },
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
      }}
      slots={{ toolbar: CustomToolbar }}
      apiRef={apiRef}
      autoHeight
      columns={columns}
      rows={rows}
      loading={loading}
      getRowHeight={() => "auto"}
      getRowId={getRowId}
      onCellClick={handleOnCellClick}
      onCellDoubleClick={handleOnCellClick}
      onCellEditStop={handleOnCellEditStop}
      pageSizeOptions={[10]}
      experimentalFeatures={{ newEditingApi: true }}
      disableColumnFilter
      disableDensitySelector
      disableColumnMenu
      disableColumnSelector
      columnVisibilityModel={{
        commentId: false,
      }}
      isCellEditable={(params) => params.row.commentBy === userEmailAddress}
      onColumnResize={() => apiRef.current.resetRowHeights()}
      getRowClassName={(params) =>
        params.row.source === 'Pre-Transaction' ? 'gray-out-row' : ''
      }
    />
  );
};
